import { createFileRoute, Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { searchAsStrings } from "@/components/portal/apps/reporting/custom/columnFilterParams";

import { RequireAuth } from "@/components/RequireAuth";
import { PortalDataShell } from "@/components/portal/PortalShell";
import { RequirePermission } from "@/components/portal/RequirePermission";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";

import { resolveParamValuesForExecute } from "@/components/portal/spaces/DateValuePickers";
import { resolveWidgetParamValues, seedSpaceParamValues } from "@/components/portal/spaces/spaceParams";
import { SpaceWidgetCell } from "@/components/portal/spaces/SpaceWidgetCell";
import { TileErrorBoundary } from "@/components/portal/spaces/TileErrorBoundary";
import type { WidgetRenderContext } from "@/components/portal/spaces/widgets/types";
import { spacesApi } from "@/services/api/spaces";

/**
 * Standalone full-screen view for a single Space widget — no sidebar/header
 * portal chrome, opened via the tile-actions "Open in new window" menu item
 * (`WidgetChrome.tsx`). Escapes the normal `/portal` layout route (the
 * `portal_` file-name prefix strips layout nesting, not the URL path — see
 * `portal_.sign-in.tsx` for the same convention) because org/auth context
 * still needs `PortalDataProvider`, just not `AppLayout`'s sidebar. See the
 * spaces-viewer-mobile-responsive-and-fullscreen-window plan (D6, D9).
 *
 * Search params mirror `$spaceId.index.tsx`'s open filter-key → value map so
 * a link built from the origin tab's current filters seeds the same values
 * here (v1 cut: cross-filters don't carry over — see plan D5). Numbers and
 * booleans come back as text (`searchAsStrings`), so `Year=2026` is not
 * dropped by a string-only filter (#1411).
 */
type WidgetWindowSearch = Record<string, string>;

export const Route = createFileRoute("/portal_/spaces/$spaceId/widget/$widgetId")({
  component: WidgetWindowPage,
  validateSearch: (search: Record<string, unknown>): WidgetWindowSearch => searchAsStrings(search),
});

function WidgetWindowPage() {
  return (
    <RequireAuth>
      <AuthedWidgetWindowPage />
    </RequireAuth>
  );
}

function AuthedWidgetWindowPage() {
  const { session } = useAuth();
  if (!session) return null; // RequireAuth redirects before this can render
  return (
    <PortalDataShell session={session}>
      <RequirePermission resource="spaces" action="view">
        <WidgetWindowContent />
      </RequirePermission>
    </PortalDataShell>
  );
}

function WidgetWindowContent() {
  const { spaceId, widgetId } = useParams({
    from: "/portal_/spaces/$spaceId/widget/$widgetId",
  });
  const search = useSearch({ from: "/portal_/spaces/$spaceId/widget/$widgetId" });
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";

  // Same query key/fn as $spaceId.index.tsx / $spaceId.edit.tsx — react-query
  // dedupes automatically, no new API surface.
  const space = useQuery({
    queryKey: ["space", orgId, spaceId],
    queryFn: () => spacesApi.getSpace(token!, orgId, spaceId),
    enabled: !!token && !!orgId && !!spaceId,
  });

  if (space.isLoading || !orgId) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (space.error) {
    return (
      <div className="flex h-dvh items-center justify-center p-6">
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {space.error instanceof Error ? space.error.message : "Failed to load space"}
        </div>
      </div>
    );
  }

  const widget = space.data?.widgets.find((w) => w.id === widgetId);
  if (!widget) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          This tile may have been removed from the space.
        </p>
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/portal/spaces/$spaceId" params={{ spaceId }}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to space
          </Link>
        </Button>
      </div>
    );
  }

  const space_ = space.data!;
  const paramValues = resolveParamValuesForExecute(
    seedSpaceParamValues(space_.parameters, search),
    space_.parameters.map((p) => ({ key: p.key, type: p.type })),
  );
  const ctx: WidgetRenderContext = {
    token: token ?? "",
    organisationId: orgId,
    paramValues: resolveWidgetParamValues(widget, paramValues),
    // #1125: the Space's own applied values, pre-binding. No `onOpenSpace`
    // is wired standalone, so a `space` target is never offered here anyway
    // (`canDrillThrough` gates on it) — threaded through regardless, for the
    // same reason as `SpaceEditorCanvas`.
    spaceParamValues: paramValues,
    // No space-wide cross-filter interaction standalone — a widget's own
    // chart-click toggle would have nothing else to filter alongside.
    activeCrossFilters: {},
  };

  // Untitled widgets fall back to just the space name — `widget.title ??
  // space_.name} — {space_.name}` would render "Procurement — Procurement".
  const pageTitle = widget.title ? `${widget.title} — ${space_.name}` : space_.name;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="flex h-dvh flex-col p-3">
        <TileErrorBoundary tileTitle={widget.title ?? undefined}>
          <div className="min-h-0 flex-1">
            <SpaceWidgetCell
              widget={widget}
              ctx={ctx}
              activeCrossFilters={{}}
              reloadToken={0}
            />
          </div>
        </TileErrorBoundary>
      </div>
    </>
  );
}
